import { isDevelopment } from '@/shared/utils/import-meta'

import {
  DEFAULT_SCREEN_SHARE_QUALITY,
  parseScreenShareQualityId,
  SCREEN_SHARE_QUALITY_PRESETS,
  type ScreenShareQualityId,
} from './screen-share-quality'

/** @deprecated Use SCREEN_SHARE_QUALITY_PRESETS.fhd.maxBitrate */
export const SCREEN_SHARE_MAX_BITRATE_BPS = SCREEN_SHARE_QUALITY_PRESETS.fhd.maxBitrate

/** @deprecated Use SCREEN_SHARE_QUALITY_PRESETS.fhd.maxFramerate */
export const SCREEN_SHARE_MAX_FRAMERATE = SCREEN_SHARE_QUALITY_PRESETS.fhd.maxFramerate

export type ScreenShareEncodingParams = {
  maxBitrate: number
  maxFramerate: number
  degradationPreference: RTCDegradationPreference
}

export const getScreenShareEncodingParams = (
  quality: ScreenShareQualityId = DEFAULT_SCREEN_SHARE_QUALITY,
): ScreenShareEncodingParams => {
  const preset = SCREEN_SHARE_QUALITY_PRESETS[parseScreenShareQualityId(quality)]
  return {
    maxBitrate: preset.maxBitrate,
    maxFramerate: preset.maxFramerate,
    degradationPreference: 'maintain-resolution',
  }
}

const isScreenShareEncodingApplied = (
  params: RTCRtpSendParameters,
  encoding: ScreenShareEncodingParams,
): boolean => {
  if (!params.encodings?.length) {
    return false
  }

  if (params.degradationPreference !== encoding.degradationPreference) {
    return false
  }

  return params.encodings.every((enc) => (
    enc.maxBitrate === encoding.maxBitrate
    && enc.maxFramerate === encoding.maxFramerate
  ))
}

/**
 * Applies screen-share sender params. No-op when already matching — avoids encoder
 * resets from repeated setParameters (e.g. on unrelated conference status updates).
 */
export const applyScreenShareSenderParams = async (
  sender: RTCRtpSender,
  quality: ScreenShareQualityId = DEFAULT_SCREEN_SHARE_QUALITY,
): Promise<boolean> => {
  if (!sender?.track || sender.track.kind !== 'video') {
    return false
  }

  if (typeof sender.getParameters !== 'function' || typeof sender.setParameters !== 'function') {
    return false
  }

  try {
    const params = sender.getParameters()
    const encoding = getScreenShareEncodingParams(quality)

    if (isScreenShareEncodingApplied(params, encoding)) {
      return true
    }

    if (!params.encodings?.length) {
      params.encodings = [{}]
    }

    params.encodings = params.encodings.map((enc) => ({
      ...enc,
      maxBitrate: encoding.maxBitrate,
      maxFramerate: encoding.maxFramerate,
      scaleResolutionDownBy: 1,
    }))
    params.degradationPreference = encoding.degradationPreference

    await sender.setParameters(params)

    if (isDevelopment()) {
      console.debug('[screen-share] Applied sender encoding', {
        trackId: sender.track.id,
        maxBitrate: encoding.maxBitrate,
        maxFramerate: encoding.maxFramerate,
        degradationPreference: encoding.degradationPreference,
      })
    }

    return true
  } catch (error) {
    console.warn('[screen-share] Failed to apply sender encoding params', error)
    return false
  }
}

export const findVideoSenderByTrackId = (
  peerConnection: RTCPeerConnection | undefined,
  trackId: string,
): RTCRtpSender | undefined => {
  return peerConnection?.getSenders().find((sender) => sender.track?.id === trackId)
}

/**
 * Applies screen-share encoding after replaceTrack / renegotiation.
 * Retries briefly — encodings may be empty until negotiation completes.
 */
export const applyScreenShareSenderParamsWithRetry = async (
  peerConnection: RTCPeerConnection | undefined,
  trackId: string,
  quality: ScreenShareQualityId = DEFAULT_SCREEN_SHARE_QUALITY,
  retries = 3,
  delayMs = 300,
): Promise<void> => {
  for (let attempt = 0; attempt < retries; attempt++) {
    const sender = findVideoSenderByTrackId(peerConnection, trackId)
    if (sender) {
      const applied = await applyScreenShareSenderParams(sender, quality)
      const encodings = sender.getParameters?.()?.encodings
      if (applied && encodings?.length) {
        return
      }
    }

    if (attempt < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }
}

/** Re-apply encoding after PC replace / renegotiation when a local screen track is live. */
export const ensureLocalScreenShareEncoding = (
  peerConnection: RTCPeerConnection | undefined,
  screenStream: MediaStream | undefined,
  quality: ScreenShareQualityId = DEFAULT_SCREEN_SHARE_QUALITY,
): void => {
  const track = screenStream?.getVideoTracks()?.at(0)
  if (!peerConnection || !track) {
    return
  }

  void applyScreenShareSenderParamsWithRetry(peerConnection, track.id, quality)
}
